/* ==========================================================================
   EDME — Product Carousel
   Click + Drag dengeli final sürüm
   ========================================================================== */

(function () {
  'use strict';

  const SELECTOR = '[data-edme-product-carousel]';
  const DRAG_THRESHOLD = 12;

  function getCarousel(section) {
    return section.querySelector('[data-edme-pc-carousel]');
  }

  function getTrack(section) {
    const carousel = getCarousel(section);
    return carousel ? carousel.querySelector('[data-edme-pc-track]') : null;
  }

  function getPrevBtn(section) {
    const carousel = getCarousel(section);
    return carousel ? carousel.querySelector('[data-edme-pc-prev]') : null;
  }

  function getNextBtn(section) {
    const carousel = getCarousel(section);
    return carousel ? carousel.querySelector('[data-edme-pc-next]') : null;
  }

  function getFirstItem(section) {
    return section.querySelector('.edme-product-carousel__item');
  }

  function getFirstProductCard(section) {
    const firstItem = getFirstItem(section);
    if (!firstItem) return null;

    return (
      firstItem.querySelector('.edme-pcard') ||
      firstItem.querySelector('.card-wrapper') ||
      firstItem.querySelector('.card') ||
      firstItem
    );
  }

  function getFirstProductVisual(section) {
    const firstItem = getFirstItem(section);
    if (!firstItem) return null;

    return (
      firstItem.querySelector('.edme-pcard__media') ||
      firstItem.querySelector('.edme-pcard__image-wrap') ||
      firstItem.querySelector('.edme-pcard__image') ||
      firstItem.querySelector('picture') ||
      firstItem.querySelector('img')
    );
  }

  function syncPromoSize(section) {
    const mediaWrap = section.querySelector('.edme-product-carousel__media-wrap');
    const firstItem = getFirstItem(section);
    const productCard = getFirstProductCard(section);
    const productVisual = getFirstProductVisual(section);

    if (!mediaWrap || !firstItem || !productCard) return;

    if (window.matchMedia('(max-width: 767px)').matches) {
      section.style.removeProperty('--edme-pc-synced-promo-width');
      section.style.removeProperty('--edme-pc-synced-promo-height');
      section.style.removeProperty('--edme-pc-arrow-top');
      return;
    }

    const itemWidth = firstItem.getBoundingClientRect().width;
    const cardHeight = productCard.getBoundingClientRect().height;
    const visualHeight = productVisual ? productVisual.getBoundingClientRect().height : 0;
    const multiplier = parseFloat(window.getComputedStyle(section).getPropertyValue('--edme-pc-promo-multiplier')) || 1;

    if (itemWidth > 0) {
      section.style.setProperty('--edme-pc-synced-promo-width', `${Math.round(itemWidth * multiplier)}px`);
    }

    if (cardHeight > 0) {
      section.style.setProperty('--edme-pc-synced-promo-height', `${Math.round(cardHeight)}px`);
    }

    if (visualHeight > 0) {
      section.style.setProperty('--edme-pc-arrow-top', `${Math.round(visualHeight / 2)}px`);
    }
  }

  function getScrollAmount(section, track) {
    const card = track?.querySelector('.edme-product-carousel__item');
    const multiplier = parseInt(section.getAttribute('data-scroll-step'), 10) || 2;

    if (!card) return 300 * multiplier;

    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;

    return (card.offsetWidth + gap) * multiplier;
  }

  function updateArrows(section) {
    const track = getTrack(section);
    const prevBtn = getPrevBtn(section);
    const nextBtn = getNextBtn(section);

    if (!track || !prevBtn || !nextBtn) return;

    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const currentLeft = track.scrollLeft;
    const threshold = 4;

    const atStart = currentLeft <= threshold;
    const atEnd = currentLeft >= maxScrollLeft - threshold;
    const noOverflow = maxScrollLeft <= threshold;

    prevBtn.disabled = noOverflow || atStart;
    nextBtn.disabled = noOverflow || atEnd;

    prevBtn.classList.toggle('is-disabled', noOverflow || atStart);
    nextBtn.classList.toggle('is-disabled', noOverflow || atEnd);
  }

  function bindDragScroll(section, track) {
    if (!track || track.dataset.dragBound === 'true') return;

    track.dataset.dragBound = 'true';

    let pointerDown = false;
    let isDragging = false;
    let startX = 0;
    let startLeft = 0;
    let activePointerId = null;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      if (event.button !== 0) return;
      if (event.target.closest('button, input, select, textarea')) return;

      pointerDown = true;
      isDragging = false;
      startX = event.clientX;
      startLeft = track.scrollLeft;
      activePointerId = event.pointerId;
    });

    track.addEventListener('pointermove', (event) => {
      if (!pointerDown) return;

      const diff = event.clientX - startX;

      if (!isDragging && Math.abs(diff) < DRAG_THRESHOLD) {
        return;
      }

      if (!isDragging) {
        isDragging = true;
        track.classList.add('is-dragging');
        track.setPointerCapture?.(activePointerId);
      }

      event.preventDefault();
      track.scrollLeft = startLeft - diff;
    });

    const endDrag = (event) => {
      if (!pointerDown) return;

      pointerDown = false;

      if (isDragging) {
        track.dataset.wasDragged = 'true';

        window.setTimeout(() => {
          delete track.dataset.wasDragged;
        }, 0);
      }

      isDragging = false;
      activePointerId = null;
      track.classList.remove('is-dragging');

      try {
        track.releasePointerCapture?.(event.pointerId);
      } catch (err) {}

      window.requestAnimationFrame(() => {
        updateArrows(section);
        syncPromoSize(section);
      });
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);

    track.addEventListener(
      'click',
      (event) => {
        if (track.dataset.wasDragged === 'true') {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  }

  function bindTrack(section, track) {
    if (!track || track.dataset.scrollBound === 'true') return;

    track.dataset.scrollBound = 'true';

    bindDragScroll(section, track);

    track.addEventListener(
      'scroll',
      () => {
        window.requestAnimationFrame(() => updateArrows(section));
      },
      { passive: true }
    );
  }

  function observeProductCard(section) {
    if (!window.ResizeObserver) return;

    const firstItem = getFirstItem(section);
    const productCard = getFirstProductCard(section);

    if (!firstItem || !productCard) return;

    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        syncPromoSize(section);
      });
    });

    observer.observe(firstItem);
    observer.observe(productCard);
  }

  function observeProductImages(section) {
    const images = section.querySelectorAll('.edme-product-carousel__item img');

    images.forEach((img) => {
      if (img.complete) {
        syncPromoSize(section);
      } else {
        img.addEventListener(
          'load',
          () => {
            syncPromoSize(section);
          },
          { once: true }
        );
      }
    });
  }

  function initSection(section) {
    if (!section || section.dataset.edmeProductCarouselInit === 'true') return;

    section.dataset.edmeProductCarouselInit = 'true';

    const track = getTrack(section);
    const prevBtn = getPrevBtn(section);
    const nextBtn = getNextBtn(section);

    bindTrack(section, track);
    observeProductImages(section);
    observeProductCard(section);

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        const activeTrack = getTrack(section);
        if (!activeTrack) return;

        activeTrack.scrollBy({
          left: -getScrollAmount(section, activeTrack),
          behavior: 'smooth'
        });

        window.requestAnimationFrame(() => {
          updateArrows(section);
          syncPromoSize(section);
        });
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const activeTrack = getTrack(section);
        if (!activeTrack) return;

        activeTrack.scrollBy({
          left: getScrollAmount(section, activeTrack),
          behavior: 'smooth'
        });

        window.requestAnimationFrame(() => {
          updateArrows(section);
          syncPromoSize(section);
        });
      });
    }

    window.addEventListener(
      'resize',
      () => {
        window.requestAnimationFrame(() => {
          updateArrows(section);
          syncPromoSize(section);
        });
      },
      { passive: true }
    );

    window.requestAnimationFrame(() => {
      updateArrows(section);
      syncPromoSize(section);
    });

    window.setTimeout(() => {
      syncPromoSize(section);
    }, 250);

    window.setTimeout(() => {
      syncPromoSize(section);
    }, 700);

    window.setTimeout(() => {
      syncPromoSize(section);
    }, 1400);
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector(SELECTOR);

    if (section) {
      section.dataset.edmeProductCarouselInit = 'false';
      initSection(section);
    }
  });
})();